import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import {
  Box,
  CardHeader,
  Grid,
  IconButton,
  TablePagination,
  Tooltip
} from '@mui/material';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import PropTypes from 'prop-types';
import { ChangeEvent, FC, useState } from 'react';
import { Card, CheckBox, Typography } from 'src/shared/components/index';
import BulkActions from './BulkActions';
import { AdminData } from '../models';
import { useTranslation } from 'react-i18next';
import { I18N } from 'src/shared/constants/constants';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';

interface AdminUserManagementOrdersCardProps {
  className?: string;
  handleEditIconClick(row: any): void;
  handleDeleteIconClick(email: any): void;
  inputAminUserData: AdminData[];
}

const applyPagination = (
  adminUserData: AdminData[],
  page: number,
  limit: number
): AdminData[] => {
  return adminUserData?.slice(page * limit, page * limit + limit);
};

const AdminUserManagementOrdersCard: FC<AdminUserManagementOrdersCardProps> = ({
  handleEditIconClick,
  inputAminUserData,
  handleDeleteIconClick
}) => {
  const [selectedAminUser, setSelectedAdminUser] = useState<string[]>([]);
  const selectedBulkActions = selectedAminUser.length > 0;
  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(5);
  const { t } = useTranslation(I18N.DEFAULT);
  const handleSelectAllAdminUserData = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    setSelectedAdminUser(
      event.target.checked
        ? inputAminUserData?.map((adminUser) => adminUser.email)
        : []
    );
  };

  const handleSelectOneCryptoOrder = (
    event: ChangeEvent<HTMLInputElement>,
    email: string
  ): void => {
    if (!selectedAminUser.includes(email)) {
      setSelectedAdminUser((prevSelected) => [...prevSelected, email]);
    } else {
      setSelectedAdminUser((prevSelected) =>
        prevSelected.filter((id) => id !== email)
      );
    }
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number
  ): void => {
    setPage(newPage);
  };

  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setLimit(parseInt(event.target.value));
  };

  const paginatedAdminUserOrders = applyPagination(
    inputAminUserData,
    page,
    limit
  );
  const selectedSomeAdminUserData =
    selectedAminUser.length > 0 &&
    selectedAminUser.length < inputAminUserData?.length;
  const selectedAlladminUserData =
    selectedAminUser.length === inputAminUserData?.length;

  return (
    <>
      <Card>
        <Container maxWidth="lg">
          <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
          >
            <Grid item xs="auto" className="flex-basic-center">
              <CheckBox
                color="primary"
                disabled={true}
                checked={selectedAlladminUserData}
                indeterminate={selectedSomeAdminUserData}
                onChange={handleSelectAllAdminUserData}
              />
              {!selectedBulkActions && (
                <span>
                  <Typography variant="h5" color="text.secondary">
                    Select All
                  </Typography>
                </span>
              )}
            </Grid>
            <Grid item xs>
              {selectedBulkActions && (
                <Box flex={1} p={2}>
                  <BulkActions />
                </Box>
              )}
            </Grid>
          </Grid>
        </Container>
      </Card>

      <Box p={1}>
        <Grid container>
          {paginatedAdminUserOrders?.map((adminUser) => {
            const isAdminUserSelected = selectedAminUser.includes(
              adminUser?.email
            );
            return (
              <Grid item xs={12} sm={6} md={4} lg={4} key={adminUser?.email}>
                <Card
                  sx={{
                    p: 1,
                    m: 1
                  }}
                >
                  <CardHeader
                    title={adminUser?.email}
                    subheader={adminUser.role.roleName}
                  />
                  <CardContent>
                    <Grid
                      container
                      direction="row"
                      spacing={4}
                      alignItems="center"
                    ></Grid>
                  </CardContent>
                  <CardActions disableSpacing>
                    <Container maxWidth="lg">
                      <Grid container direction="row" alignItems="center">
                        <Grid item xs={8}>
                          <CheckBox
                            color="primary"
                            checked={isAdminUserSelected}
                            disabled={true}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                              handleSelectOneCryptoOrder(
                                event,
                                adminUser?.email
                              )
                            }
                            value={isAdminUserSelected}
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <Tooltip title={t('delete')} arrow>
                            <IconButton
                              color="inherit"
                              size="small"
                              className="RecentOrderDeleteButton"
                              onClick={() =>
                                handleDeleteIconClick(adminUser?.email)
                              }
                            >
                              <DeleteTwoToneIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                        <Grid item xs={2}>
                          <Tooltip title="Edit User" arrow>
                            <IconButton
                              className="RecentOrderEditButton"
                              color="inherit"
                              size="small"
                              onClick={() => handleEditIconClick(adminUser)}
                            >
                              <EditTwoToneIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    </Container>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      <Card>
        <Box p={2}>
          <TablePagination
            component="div"
            count={inputAminUserData.length}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
            page={page}
            rowsPerPage={limit}
            rowsPerPageOptions={[5, 10, 25, 30]}
          />
        </Box>
      </Card>
    </>
  );
};

AdminUserManagementOrdersCard.propTypes = {
  inputAminUserData: PropTypes.array.isRequired
};

AdminUserManagementOrdersCard.defaultProps = {
  inputAminUserData: []
};

export default AdminUserManagementOrdersCard;
